/**
 *  KeyHandler is an object that deals with adding listeners on the main dom Node to detect key stroke
 *  combinations and accordingly triggers different operations
 *
 *  @copyright Copyright 2015 The MathWorks, Inc.
 */

define([
    "dojo/_base/declare",
    "dojo/sniff",
    "dojo/_base/event",
    "dojo/query",
    "dojo/_base/lang",
    "dojo/on",
    "dijit/Destroyable",

    "mw-keybindings/BindingUtils",
    "mw-keybindings/Platforms"

], function (declare, sniff, event, query, lang, on, Destroyable, BindingUtils,Platforms) {

    return declare(Destroyable, {

        constructor: function(keyBindings) {
            this._listeners =[];
            this._keyBindings = keyBindings;
            this._attachListeners();

            // An Enum representing the different states of the binding when  triggered by the
            // keyDown function when a keystroke combination is hit by the user.
            this.BINDING_EXECUTION_STATUS = {
                BINDING_NOT_FOUND : 0,
                BINDING_EXECUTION_SUCCESSFUL : 1,
                EXECUTION_INVALID_FOR_BINDING : 2
            }
        },

        /**
         * Attach listeners on the main domNode.
         *
         * @param selector
         */
        _attachListeners: function () {
            //The keydown event is heard at the document level.
            this.own(on(document, "keydown", lang.hitch(this, "onKeyDown") ));
        },

        /**
         * Remove the listeners on the domNode to make the KeyHandler incapable of listening to any
         * key events on the dom.
         *
         * @private
         */
        _removeListeners: function () {
            this._listeners.forEach(function (connection) {
                    connection.remove();
                });
        },


        /**
         * Handles key down event for all the key strokes made.
         *
         * @param evt  the event
         */
        onKeyDown: function (evt) {
            var binding = this._lookupBinding(evt);
            if(!binding) {
                // A Binding is not found for the keyboard combination.
                return this.BINDING_EXECUTION_STATUS.BINDING_NOT_FOUND;
            }

            var isBindingExecuted = this._executeBindingForDomNode(binding, evt.target, evt);
            if(isBindingExecuted) {
                // The Binding has successfully triggered an action(s).
                return this.BINDING_EXECUTION_STATUS.BINDING_EXECUTION_SUCCESSFUL;
            } else {
                // The Binding is invalid or does not have a matching action for the target Node.
                return this.BINDING_EXECUTION_STATUS.EXECUTION_INVALID_FOR_BINDING;

            }
        },


        /**
         * Retrieve any binding if registered for the given key stroke combination which
         * is passed in, in the form of an event.
         *
         * @param event
         * @returns binding
         * @private
         */
        _lookupBinding: function(event) {
            var modifierKeys = [event.ctrlKey, event.shiftKey,event.altKey, event.metaKey];

            // the identifier now includes the platform because platform-specific bindings can be made
            var platform = undefined;
            var browserInfo = navigator.userAgent.toLowerCase(); // sniff("is-windows") does not seem to work
            if (sniff("mac")){
                platform = Platforms.Mac;
            } else if ((browserInfo.indexOf("windows")) > -1) {
                platform = Platforms.Windows;
            } else if((browserInfo.indexOf("linux")) > -1) {
                platform = Platforms.Linux;
            } else {
                platform = Platforms.Unspecified;
            }

            // first create an identifier for the platform we are running on and see if there is a binding
            var keySequenceIdentifier = BindingUtils.generateKeySequenceIdentifier(
                modifierKeys, event.keyCode, platform);
            var binding = this._keyBindings.getBindingFromKeySequenceIdentifier(keySequenceIdentifier);

            // if not found, then see if there is a binding with no platform specified
            if ( binding === undefined ) {
                keySequenceIdentifier = BindingUtils.generateKeySequenceIdentifier(
                    modifierKeys, event.keyCode, Platforms.Unspecified);
                binding = this._keyBindings.getBindingFromKeySequenceIdentifier(keySequenceIdentifier);
            }

            if ( binding === undefined ) {
                return null;
            }

            return binding;

        },

        /**
         * Execute the Binding for the targeted node or current node
         *
         * @param binding
         * @param currentNode
         * @param they keyDown event that is triggered
         * @private
         */
        _executeBindingForDomNode: function (binding, currentNode ,event ) {

            if(!currentNode) {
                return false;
            }
            var actionInfo = binding.getActionInfoForNode(currentNode);
            if(actionInfo) {
                 binding.executeAction(actionInfo.actionId);
                if(actionInfo.preventDefault) {
                    event.preventDefault();
                }
                if(!actionInfo.stopPropagation) {
                    this._executeBindingForDomNode(binding, currentNode.parentElement , event);
                }
                return true;

            } else {
                this._executeBindingForDomNode(binding, currentNode.parentElement, event);
            }
            return false;

        },

        /**
         *  Get the selector matching to the currentNode from the selector list.
         *
         * @param selectorList
         * @param currentNode
         * @returns selector
         */
        getMatchingSelector : function (selectorList, currentNode) {
            var _selector;
            selectorList.some(function(selector) {
                   if(currentNode.matches(selector)) {
                       _selector =  selector;
                       return;
                   }
            });

            return _selector;
        }

    });
});

